import { CLUSTER, touchReserved } from "../hud/touch-slots.ts";
import { FRAMES, HUD } from "odyssey-shared";
import { drawFrame, pageRoom, type PlaceRoom, type PlaceSize, type WindowPlace } from "../../ui/kit/place.ts";
import type { TipPlace } from "../bar/tip-place.ts";
import { HUD_SLOTS, resolveSlot, type HudArea, type SlotRect } from "../hud/slots.ts";

/**
 * Where the Inventory window and its tooltip stand (P2.8-T11, ADR-091 decision 21; the design's `inventory` and
 * `item-tooltip` cards in `ItemSurfaces.jsx`, a visual reference). Pure: the window and the tooltip's view lay
 * themselves out from these.
 */

/**
 * The window's geometry in logical pixels, the design's: 300 by 236 in the right window slot, 14 from the area's right
 * edge and 44 from its top (326,44 on the 640 by 360 grid), its body padded `padding` inside the frame (the design's 5,
 * where the kit's other windows keep 6); the tool row above the grid (P2.8-T45, ADR-125 decision 4), its search field
 * filling it and the Filter and Sort buttons `toolGap` apart, each at most `toolMax` wide with its text cut by an ellipsis beyond that, so a longer name never takes the field's room, Filter's menu `menuGap` under the row and at least
 * `filterMenuWidth` wide, and the grid `rowGap` under the row; a grid of 10 columns by 5 rows of 24 px slots 1 apart,
 * 249 by 124; a bag slot's item card (P2.8-T46, `ITEM_CARD.width` in
 * `card.tsx`) 6 left of the window where there is room and `tipGap` from its slot where there is not; the combat note
 * `noteGap` under the grid, its glyph `noteGlyphGap` from its words. `minHeight` is the least the window is ever drawn:
 * its header, the tool row, two rows of slots and the footer, the rest reached by scrolling down; a window that cannot
 * stand that tall above the touch controls stands beside them (`bagWindowBox`). `clearTop` is the highest the window
 * ever rises: under a band at the area's top right, at the HUD's margin and `feedbackHeight` logical pixels tall, with
 * a gutter below it, which the window leaves clear.
 */
export const BAG_WINDOW = { width: 300, height: 236, right: 14, top: 44, columns: 10, rows: 5, slot: 24, gap: 1, padding: 5, toolGap: 3, toolMax: 96, rowGap: 2, menuGap: 3, filterMenuWidth: 110, tipBeside: 6, tipGap: 2, minHeight: 144, noteGap: 4, noteGlyphGap: 3, feedbackHeight: 26, clearTop: HUD.margin + 26 + HUD.gutter } as const;

/**
 * The window's box on an area: the design's size, hung from the right edge and the top, and on an area too small for
 * that, moved up as far as `clearTop` and then shortened so it stays whole above the XP bar over the ability bar,
 * and narrowed to the area's margins. Its content scrolls down inside a shortened window, never across. On a touch
 * page where that leaves less than `minHeight` (a phone held landscape), the window stands left of the action
 * buttons instead, from the area's margin to the bottom margin, so its grid always shows two rows at least; it
 * stands over the ability bar and the stick then, which the player has closed the window to use.
 *
 * @param area The HUD's area, whole logical pixels.
 * @returns The box from the area's top left, whole logical pixels.
 * @example
 * bagWindowBox({ width: 640, height: 360 });
 * // { left: 326, top: 44, width: 300, height: 236 }
 */
export function bagWindowBox(area: HudArea): SlotRect {
  if (bagStandsBeside(area)) {
    return bagBesideBox(area);
  }
  const floor = bagFloor(area);
  const width = Math.min(BAG_WINDOW.width, area.width - 2 * HUD.margin);
  const left = Math.max(HUD.margin, area.width - BAG_WINDOW.right - width);
  const top = Math.max(BAG_WINDOW.clearTop, Math.min(BAG_WINDOW.top, floor - BAG_WINDOW.height));
  const height = Math.max(BAG_WINDOW.minHeight, Math.min(BAG_WINDOW.height, floor - top));
  return { left, top, width, height };
}

/**
 * The lowest the window's bottom edge may stand above the touch controls: a gutter above the XP bar over the ability
 * bar, and on a touch page above the action buttons' corner too.
 *
 * @param area The HUD's area.
 * @returns The floor in whole logical pixels from the area's top.
 */
function bagFloor(area: HudArea): number {
  const cluster = area.touch === true ? area.height - (area.safe?.bottom ?? 0) - CLUSTER.height : Number.POSITIVE_INFINITY;
  return Math.min(resolveSlot(HUD_SLOTS.xp, area).top, cluster) - HUD.gutter;
}

/**
 * Whether the window stands beside the action buttons: on a touch page where the room above them, from `clearTop`
 * down, is under `minHeight`.
 *
 * @param area The HUD's area.
 * @returns True for a touch page too short to stand the window above the buttons.
 */
function bagStandsBeside(area: HudArea): boolean {
  return area.touch === true && bagFloor(area) - BAG_WINDOW.clearTop < BAG_WINDOW.minHeight;
}

/**
 * The window's box on a touch page too short to stand it above the action buttons: its right edge a gutter left of the
 * buttons' corner, its width the design's or what that leaves inside the margin, its top at the margin and its height
 * what the margins leave, never under `minHeight`.
 *
 * @param area The HUD's area, with `touch` set.
 * @returns The box from the area's top left, whole logical pixels.
 */
function bagBesideBox(area: HudArea): SlotRect {
  const safe = area.safe ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const buttons = touchReserved(area)[0];
  const right = (buttons?.left ?? area.width) - HUD.gutter;
  const width = Math.max(1, Math.min(BAG_WINDOW.width, right - safe.left - HUD.margin));
  const top = safe.top + HUD.margin;
  const height = Math.max(BAG_WINDOW.minHeight, Math.min(BAG_WINDOW.height, area.height - safe.bottom - HUD.margin - top));
  return { left: right - width, top, width, height };
}

/**
 * The room the window is held inside once it has been moved (ADR-061's amendment "a window can be moved"): the area
 * less the HUD's margin, starting at `clearTop`, so the window is whole on the page and never under the staff
 * Feedback button; starting at the margin where the window stands beside the action buttons, so a window moved or
 * resized there is held inside the room its layout box used. It may stand over the ability bar there; the viewer put
 * it so.
 *
 * @param area The HUD's area.
 * @returns The room, whole logical pixels.
 * @example
 * bagRoom({ width: 640, height: 360 });
 * // { left: 4, top: 32, right: 636, bottom: 356 }
 */
export function bagRoom(area: HudArea): PlaceRoom {
  return pageRoom(area, HUD.margin, bagStandsBeside(area) ? HUD.margin : BAG_WINDOW.clearTop);
}

/**
 * The window's box as drawn (the kit's `drawFrame`, which the window's own frame draws by): `bagWindowBox`'s for a
 * window never moved or resized; a moved one at the kept place held inside `bagRoom`; a resized one at the kept size
 * held between the window's limits and the room, in whole slots (ADR-061's amendment "every game window moves and
 * resizes", items 7 and 8). A page with no room for the window's minimum draws it as its layout makes it.
 *
 * @param area The HUD's area.
 * @param wish The place kept, or null for a window never moved.
 * @param size The size kept, or null for a window never resized.
 * @returns The box from the area's top left, whole logical pixels.
 * @example
 * bagBoxAt({ width: 640, height: 360 }, { left: 20, top: 500 });
 * // { left: 20, top: 120, width: 300, height: 236 }
 */
export function bagBoxAt(area: HudArea, wish: WindowPlace | null, size: PlaceSize | null = null): SlotRect {
  return drawFrame("inventory", bagWindowBox(area), { place: wish, size }, bagRoom(area));
}

/**
 * The width the grid has inside a window of a width: the window less its frame's insets and its body's padding, which
 * are all the chrome the grid gives up (the design's spare pixels are space the grid fills, not chrome).
 *
 * @param width The window's width as drawn, in logical pixels.
 * @returns The width in logical pixels.
 * @example
 * bagGridWidth(300);
 * // 282
 */
function bagGridWidth(width: number): number {
  return width - FRAMES.window.insets.left - FRAMES.window.insets.right - 2 * BAG_WINDOW.padding;
}

/**
 * How many columns the grid shows in a window of a width: as many whole slots, one pitch apart, as the width inside the
 * frame and the padding holds (eleven at the design's 300); one column at least. The rows past the window's height
 * scroll.
 *
 * @param width The window's width as drawn, in logical pixels.
 * @returns The column count.
 * @example
 * bagColumns(325);
 * // 12
 */
export function bagColumns(width: number): number {
  const pitch = BAG_WINDOW.slot + BAG_WINDOW.gap;
  return Math.max(1, Math.floor((bagGridWidth(width) + BAG_WINDOW.gap) / pitch));
}

/**
 * How far the grid stands from the body's left edge: half of what its whole slots leave over, rounded down, so the space
 * left and right of the grid differ by at most one logical pixel. Under one pitch is ever left over, and a window kept
 * on the window's steps leaves the same at every width.
 *
 * @param width The window's width as drawn, in logical pixels.
 * @returns The inset in logical pixels, never negative.
 * @example
 * bagGridInset(300);
 * // 4
 */
export function bagGridInset(width: number): number {
  const used = bagColumns(width) * (BAG_WINDOW.slot + BAG_WINDOW.gap) - BAG_WINDOW.gap;
  return Math.max(0, Math.floor((bagGridWidth(width) - used) / 2));
}

/**
 * The height of the empty state, which stands in the grid's place: the design's five rows, and a taller window's
 * whole rows, so an emptied or a filled bag moves nothing.
 *
 * @param height The window's height as drawn, in logical pixels.
 * @returns The height in logical pixels.
 * @example
 * bagEmptyHeight(236);
 * // 124
 */
export function bagEmptyHeight(height: number): number {
  const pitch = BAG_WINDOW.slot + BAG_WINDOW.gap;
  const design = BAG_WINDOW.rows * pitch - BAG_WINDOW.gap;
  const rows = Math.floor((height - (BAG_WINDOW.height - design) + BAG_WINDOW.gap) / pitch);
  return Math.max(design, rows * pitch - BAG_WINDOW.gap);
}

/**
 * How many cells the grid draws: the design's 50, and whole rows of its columns beyond them for a bag, or a slot the server
 * still holds, past the fiftieth.
 *
 * @param size The bag's size, or null before the server has said it.
 * @param held How many slots the store holds.
 * @param columns How many columns the grid shows (`bagColumns`); the design's ten unless given.
 * @returns The cell count, a multiple of the columns.
 * @example
 * bagCells(20, 20);
 * // 50
 */
export function bagCells(size: number | null, held: number, columns: number = BAG_WINDOW.columns): number {
  const cells = Math.max(BAG_WINDOW.rows * BAG_WINDOW.columns, size ?? 0, held);
  return Math.ceil(cells / columns) * columns;
}

/**
 * Where a bag slot's tooltip, the item card (P2.8-T46, ADR-125 decision 5.1), stands: the design's "flips to stay 4 px
 * inside the screen and never covers the hovered slot". Left of the window, the design's 6 from it, its top at the
 * slot's and held inside the area's margin, while the area has that room; otherwise over the window, its left at the
 * slot's and held inside the margin, under a slot in the area's upper half and above one in its lower half, or on the
 * other side of the slot where the card's height does not fit the first; and where it fits neither, beside the slot,
 * left of it or else right of it, its top held inside the margin. A card whose height is not yet measured (0) is
 * placed as a short one.
 *
 * @param slot The slot's rectangle from the area's top left as the page measured it, or null before it is measured.
 * @param area The HUD's area.
 * @param width The card's width.
 * @param box The window's box as drawn (`bagBoxAt`); where its layout puts it unless given.
 * @param height The card's height as laid out, in logical pixels, or 0 before it is measured.
 * @returns The place.
 * @example
 * bagTipPlace({ left: 362, top: 91, width: 24, height: 24 }, { width: 640, height: 360 }, 172, undefined, 150);
 * // { left: 148, top: 91, bottom: null }
 */
export function bagTipPlace(slot: SlotRect | null, area: HudArea, width: number, box: SlotRect = bagWindowBox(area), height = 0): TipPlace {
  const lowest = Math.max(HUD.margin, area.height - HUD.margin - Math.ceil(height));
  const beside = box.left - BAG_WINDOW.tipBeside - width;
  if (beside >= HUD.margin) {
    return { left: beside, top: Math.min(lowest, Math.max(HUD.margin, slot?.top ?? box.top)), bottom: null };
  }
  const at = slot ?? { left: box.left, top: box.top, width: 0, height: 0 };
  const left = Math.max(HUD.margin, Math.min(at.left, area.width - HUD.margin - width));
  const below = at.top + at.height + BAG_WINDOW.tipGap;
  const fitsBelow = below + height <= area.height - HUD.margin;
  const fitsAbove = at.top - BAG_WINDOW.tipGap - height >= HUD.margin;
  const under = { left, top: below, bottom: null };
  const over = { left, top: null, bottom: area.height - at.top + BAG_WINDOW.tipGap };
  const upper = at.top + at.height / 2 < area.height / 2;
  if (upper ? fitsBelow : !fitsAbove && fitsBelow) {
    return under;
  }
  if (fitsAbove) {
    return over;
  }
  const top = Math.min(lowest, Math.max(HUD.margin, at.top));
  const leftOf = at.left - BAG_WINDOW.tipGap - width;
  if (leftOf >= HUD.margin) {
    return { left: leftOf, top, bottom: null };
  }
  return { left: Math.min(at.left + at.width + BAG_WINDOW.tipGap, area.width - HUD.margin - width), top, bottom: null };
}
